import React from 'react';
import {
  useFormContext,
  useWatch,
} from '@grnhse/seedling/lib/birch/components/forms/form';
import { withLocalePrefix } from 'shared/utils/translation';
import { useUserContext } from 'talent_matching/contexts/user_context';
import { AppliedOnType } from 'talent_matching/types';
import type AdvancedFilter from 'talent_matching/components/filters/advanced_filter';
import AppliedFilterChip, {
  type AppliedFilterChipProps,
} from 'talent_matching/components/applied_filters/applied_filter_chip';
import AppliedAdvancedFilterChip from 'talent_matching/components/applied_filters/applied_advanced_filter_chip';
import BaseAppliedFiltersRow from 'talent_matching/components/shared/base_applied_filters_row';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import { formatShortDate } from 'talent_search/lib/utils';

const t = withLocalePrefix('talent_matching.filters.applied');

type Props = {
  formDefaults: Record<string, unknown>;
};

type FormValues = {
  can_have: string[];
  must_have: string[];
  does_not_have: string[];
  milestone: Item | null;
  stages: Item[] | null;
  include_rejection_reasons: Item[];
  exclude_rejection_reasons: Item[];
  scorecard_performance: Item | null;
  scorecard_attribute_filters: Item[];
  scorecard_attribute_match: string | null;
  no_strong_no_scores: boolean;
  exclude_hired: boolean;
  without_candidate_tags: Item[];
  appliedOnStart: string | null;
  appliedOnEnd: string | null;
  appliedOnType: string | null;
  jobs: Item[];
  departments: Item[];
  offices: Item[];
  advancedFilters: AdvancedFilter[];
};

const FORM_FIELD_KEYS: (keyof FormValues)[] = [
  'can_have',
  'must_have',
  'does_not_have',
  'milestone',
  'stages',
  'include_rejection_reasons',
  'exclude_rejection_reasons',
  'scorecard_performance',
  'scorecard_attribute_filters',
  'scorecard_attribute_match',
  'no_strong_no_scores',
  'exclude_hired',
  'without_candidate_tags',
  'appliedOnStart',
  'appliedOnEnd',
  'appliedOnType',
  'jobs',
  'departments',
  'offices',
  'advancedFilters',
];

const APPLIED_ON_TEXT_MAPPING: {
  [key in AppliedOnType]: (start: string, end: string) => string;
} = {
  [AppliedOnType.TODAY]: () => t('today'),
  [AppliedOnType.YESTERDAY]: () => t('yesterday'),
  [AppliedOnType.LAST_SEVEN_DAYS]: () => t('last_seven_days'),
  [AppliedOnType.LAST_6_MONTHS]: () => t('last_6_months'),
  [AppliedOnType.LAST_12_MONTHS]: () => t('last_12_months'),
  [AppliedOnType.LAST_2_YEARS]: () => t('last_2_years'),
  [AppliedOnType.BETWEEN]: (start, end) => t('between', { start, end }),
  [AppliedOnType.ON_OR_AFTER]: (start) => t('on_or_after', { start }),
  [AppliedOnType.ON_OR_BEFORE]: (_start, end) => t('on_or_before', { end }),
};

function itemLabels(items: Item[]): string {
  return items.map((i) => i.label).join(', ');
}

function buildFilters(
  values: FormValues,
  userDateFormat: string
): Omit<AppliedFilterChipProps, 'formDefaults'>[] {
  const filters: Omit<AppliedFilterChipProps, 'formDefaults'>[] = [];

  if (values.can_have?.length > 0) {
    filters.push({
      label: t('keywords.can_have', { keywords: values.can_have.join(', ') }),
      filterKeys: ['can_have'],
      uniqueKey: 'keywords.can_have',
    });
  }

  if (values.must_have?.length > 0) {
    filters.push({
      label: t('keywords.must_have', { keywords: values.must_have.join(', ') }),
      filterKeys: ['must_have'],
      uniqueKey: 'keywords.must_have',
    });
  }

  if (values.does_not_have?.length > 0) {
    filters.push({
      label: t('keywords.does_not_have', {
        keywords: values.does_not_have.join(', '),
      }),
      filterKeys: ['does_not_have'],
      uniqueKey: 'keywords.does_not_have',
    });
  }

  if (values.milestone) {
    filters.push({
      label: t('milestone', { milestones: values.milestone.label }),
      filterKeys: ['milestone'],
      uniqueKey: 'milestone',
    });
  }

  if (values.stages?.length) {
    filters.push({
      label: t('stages', { stages: itemLabels(values.stages) }),
      filterKeys: ['stages'],
      uniqueKey: 'stages',
    });
  }

  if (values.include_rejection_reasons?.length > 0) {
    filters.push({
      label: t('include_rejection_reasons', {
        reasons: itemLabels(values.include_rejection_reasons),
      }),
      filterKeys: ['include_rejection_reasons'],
      uniqueKey: 'include_rejection_reasons',
    });
  }

  if (values.exclude_rejection_reasons?.length > 0) {
    filters.push({
      label: t('exclude_rejection_reasons', {
        reasons: itemLabels(values.exclude_rejection_reasons),
      }),
      filterKeys: ['exclude_rejection_reasons'],
      uniqueKey: 'exclude_rejection_reasons',
    });
  }

  if (values.scorecard_performance) {
    filters.push({
      label: t('scorecard_performance', {
        performance: values.scorecard_performance.label,
      }),
      filterKeys: ['scorecard_performance'],
      uniqueKey: 'scorecard_performance',
    });
  }

  if (values.scorecard_attribute_filters?.length > 0) {
    const matchType = values.scorecard_attribute_match ?? 'any';
    const key =
      matchType === 'all'
        ? 'scorecard_attributes_all'
        : 'scorecard_attributes_any';
    filters.push({
      label: t(key, {
        attributes: itemLabels(values.scorecard_attribute_filters),
      }),
      filterKeys: ['scorecard_attribute_filters'],
      uniqueKey: 'scorecard_attribute_filters',
    });
  }

  if (values.no_strong_no_scores) {
    filters.push({
      label: t('no_strong_no_scores'),
      filterKeys: ['no_strong_no_scores'],
      uniqueKey: 'no_strong_no_scores',
    });
  }

  if (values.exclude_hired) {
    filters.push({
      label: t('exclude_hired'),
      filterKeys: ['exclude_hired'],
      uniqueKey: 'exclude_hired',
    });
  }

  if (values.without_candidate_tags?.length > 0) {
    filters.push({
      label: t('without_candidate_tags', {
        tags: itemLabels(values.without_candidate_tags),
      }),
      filterKeys: ['without_candidate_tags'],
      uniqueKey: 'without_candidate_tags',
    });
  }

  if (values.appliedOnType && (values.appliedOnStart || values.appliedOnEnd)) {
    const startDateText =
      formatShortDate(values.appliedOnStart, userDateFormat) || '...';
    const endDateText =
      formatShortDate(values.appliedOnEnd, userDateFormat) || '...';
    const whenText = APPLIED_ON_TEXT_MAPPING[
      values.appliedOnType as AppliedOnType
    ]?.(startDateText, endDateText);

    if (whenText) {
      filters.push({
        label: t('last_activity_when', { when: whenText }),
        filterKeys: ['appliedOnStart', 'appliedOnEnd', 'appliedOnType'],
        uniqueKey: 'appliedOn',
      });
    }
  }

  if (values.jobs?.length > 0) {
    filters.push({
      label: t('jobs', { jobs: itemLabels(values.jobs) }),
      filterKeys: ['jobs'],
      uniqueKey: 'jobs',
    });
  }

  if (values.departments?.length > 0) {
    filters.push({
      label: t('departments', { departments: itemLabels(values.departments) }),
      filterKeys: ['departments'],
      uniqueKey: 'departments',
    });
  }

  if (values.offices?.length > 0) {
    filters.push({
      label: t('offices', { offices: itemLabels(values.offices) }),
      filterKeys: ['offices'],
      uniqueKey: 'offices',
    });
  }

  return filters;
}

const RediscoveryAppliedFilters = ({ formDefaults }: Props) => {
  const { control } = useFormContext();
  const { userDateFormat } = useUserContext();
  const watchedValues = useWatch({ control, name: FORM_FIELD_KEYS });

  const values: FormValues = Object.fromEntries(
    FORM_FIELD_KEYS.map((key, i) => [key, watchedValues[i]])
  ) as FormValues;

  const appliedAdvancedFilters = (values.advancedFilters ?? []).filter(
    (f: AdvancedFilter) => f.queryKey?.() && f.queryParamValue?.()
  );

  const chips: React.ReactNode[] = [
    ...buildFilters(values, userDateFormat).map((filter) => (
      <AppliedFilterChip
        key={filter.uniqueKey}
        formDefaults={formDefaults}
        {...filter}
      />
    )),
    ...appliedAdvancedFilters.map((filter: AdvancedFilter) => (
      <AppliedAdvancedFilterChip
        filter={filter}
        key={filter.reactKey}
        fieldName="advancedFilters"
      />
    )),
  ];

  return <BaseAppliedFiltersRow chips={chips} />;
};

export default RediscoveryAppliedFilters;
